import React, { useEffect, useMemo, useState } from 'react';
import { EmptyState, PageHeader } from '../../components/ui';
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { apiErrorMessage, usersService, mechanicsService } from '../../services/api';
import { useCachedData } from '../../hooks/useCachedData';
import { Mechanic, CreateMechanicDto, User, UserRole, CreateMechanicWithUserDto } from '../../types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  userId: Yup.string().required('User is required'),
  specialization: Yup.string().required('Specialization is required'),
  yearsOfExperience: Yup.number()
    .required('Years of experience is required')
    .min(0, 'Years of experience must be at least 0')
    .integer('Years of experience must be a whole number'),
  isAvailable: Yup.boolean(),
});

const newMechanicValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),
  specialization: Yup.string().required('Specialization is required'),
  yearsOfExperience: Yup.number()
    .required('Years of experience is required')
    .min(0, 'Years of experience must be at least 0')
    .integer('Years of experience must be a whole number'),
  isAvailable: Yup.boolean(),
});

const NO_MECHANICS: Mechanic[] = [];
const NO_USERS: User[] = [];

const MechanicsPage: React.FC = () => {
  const { user } = useAuth();
  // From the session cache first, then reloaded: returning here paints at once.
  // The two lists load side by side; the accounts used to wait for the mechanics.
  const mechanicsData = useCachedData('mechanics', () => mechanicsService.getAllMechanics());
  // Mechanic accounts for this shop; the role filter is applied server-side.
  const mechanicUsersData = useCachedData(`users:${UserRole.MECHANIC}`, () =>
    usersService.getAllUsers(UserRole.MECHANIC),
  );
  const mechanics = mechanicsData.data ?? NO_MECHANICS;
  const loading = mechanicsData.loading;
  const setMechanics = mechanicsData.mutate;
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addNewMechanicDialogOpen, setAddNewMechanicDialogOpen] = useState(false);

  const fetchMechanics = async () => {
    await Promise.all([mechanicsData.reload(), mechanicUsersData.reload()]);
  };

  // Accounts that do not already have a mechanic record can be linked.
  const availableUsers = useMemo(() => {
    const mechanicUserIds = new Set(mechanics.map((mechanic) => mechanic.userId));
    return (mechanicUsersData.data ?? NO_USERS).filter((u: User) => !mechanicUserIds.has(u.id));
  }, [mechanics, mechanicUsersData.data]);

  useEffect(() => {
    if (mechanicsData.error) setError(apiErrorMessage(mechanicsData.error, 'Failed to fetch mechanics'));
  }, [mechanicsData.error]);

  useEffect(() => {
    // Previously this 404'd on every load, because /users had no controller —
    // which is why the list of linkable accounts was always empty.
    if (mechanicUsersData.error) console.error('Failed to fetch available users:', mechanicUsersData.error);
  }, [mechanicUsersData.error]);

  const handleAddDialogOpen = () => {
    setAddDialogOpen(true);
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
  };

  const handleAddNewMechanicDialogOpen = () => {
    setAddNewMechanicDialogOpen(true);
  };

  const handleAddNewMechanicDialogClose = () => {
    setAddNewMechanicDialogOpen(false);
  };

  const handleAddMechanic = async (values: CreateMechanicDto, { resetForm }: { resetForm: () => void }) => {
    try {
      // Add shopId to the mechanic data from the current user's shop
      const mechanicData: CreateMechanicDto = {
        ...values,
        shopId: user?.shopId || '',
      };

      await mechanicsService.createMechanic(mechanicData);
      setSuccessMessage('Mechanic added successfully');
      resetForm();
      handleAddDialogClose();
      await fetchMechanics();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add mechanic');
    }
  };

  const handleAddMechanicWithUser = async (values: CreateMechanicWithUserDto, { resetForm }: { resetForm: () => void }) => {
    try {
      // Add shopId to the mechanic data from the current user's shop if not present
      const mechanicData: CreateMechanicWithUserDto = {
        ...values,
        shopId: values.shopId || user?.shopId || '',
      };

      await mechanicsService.createMechanicWithUser(mechanicData);
      setSuccessMessage('Mechanic and user account created successfully');
      resetForm();
      handleAddNewMechanicDialogClose();
      await fetchMechanics();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create mechanic with user account');
    }
  };

  const handleDeleteMechanic = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this mechanic?')) return;

    try {
      await mechanicsService.deleteMechanic(id);
      setMechanics(prev => prev.filter(m => m.id !== id));
      setSuccessMessage('Mechanic deleted successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete mechanic');
    }
  };

  if (loading && mechanics.length === 0) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        title="Mechanics"
        subtitle="The technicians who can be assigned to jobs at your shop."
        actions={
          <>
            <Button
              variant="outlined"
              onClick={handleAddDialogOpen}
              disabled={availableUsers.length === 0}
            >
              Add existing user
            </Button>
            <Button variant="contained" onClick={handleAddNewMechanicDialogOpen}>
              Create mechanic
            </Button>
          </>
        }
      />

      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Specialization</TableCell>
              <TableCell>Experience (Years)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mechanics.length > 0 ? (
              mechanics.map((mechanic) => (
                <TableRow key={mechanic.id}>
                  <TableCell>{mechanic.user?.name}</TableCell>
                  <TableCell>{mechanic.user?.email}</TableCell>
                  <TableCell>{mechanic.specialization}</TableCell>
                  <TableCell>{mechanic.yearsOfExperience}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: mechanic.isAvailable ? 'success.main' : 'error.main',
                        fontWeight: 'bold',
                      }}
                    >
                      {mechanic.isAvailable ? 'Available' : 'Unavailable'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleDeleteMechanic(mechanic.id)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                  <EmptyState
                    dense
                    title="No mechanics yet"
                    description="Create a mechanic account so jobs can be assigned to someone."
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Mechanic Dialog */}
      <Dialog open={addDialogOpen} onClose={handleAddDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Mechanic</DialogTitle>
        <Formik
          initialValues={{
            userId: '',
            specialization: '',
            yearsOfExperience: 0,
            isAvailable: true,
            shopId: user?.shopId || '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleAddMechanic}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth error={touched.userId && Boolean(errors.userId)}>
                      <InputLabel>Select User</InputLabel>
                      <Select
                        name="userId"
                        value={values.userId}
                        label="Select User"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        {availableUsers.length > 0 ? (
                          availableUsers.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              {user.name} ({user.email})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No available users with mechanic role</MenuItem>
                        )}
                      </Select>
                      {touched.userId && errors.userId && (
                        <FormHelperText>{errors.userId}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      name="specialization"
                      label="Specialization"
                      value={values.specialization}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.specialization && Boolean(errors.specialization)}
                      helperText={touched.specialization && errors.specialization}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      name="yearsOfExperience"
                      label="Years of Experience"
                      type="number"
                      value={values.yearsOfExperience}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.yearsOfExperience && Boolean(errors.yearsOfExperience)}
                      helperText={touched.yearsOfExperience && errors.yearsOfExperience}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <InputLabel>Availability</InputLabel>
                      <Select
                        name="isAvailable"
                        value={values.isAvailable ? 'true' : 'false'}
                        label="Availability"
                        onChange={(e) => {
                          handleChange({
                            target: {
                              name: 'isAvailable',
                              value: e.target.value === 'true',
                            },
                          } as React.ChangeEvent<any>);
                        }}
                      >
                        <MenuItem value="true">Available</MenuItem>
                        <MenuItem value="false">Unavailable</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleAddDialogClose}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || availableUsers.length === 0}
                >
                  Add Mechanic
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Add New Mechanic With User Dialog */}
      <Dialog open={addNewMechanicDialogOpen} onClose={handleAddNewMechanicDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Mechanic</DialogTitle>
        <Formik
          initialValues={{
            name: '',
            email: '',
            password: '',
            specialization: '',
            yearsOfExperience: 0,
            isAvailable: true,
            shopId: user?.shopId || '',
          }}
          validationSchema={newMechanicValidationSchema}
          onSubmit={handleAddMechanicWithUser}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      User Account Information
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Full Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email Address"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      name="password"
                      label="Password"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                    <Divider />
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                      Mechanic Information
                    </Typography>
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      name="specialization"
                      label="Specialization"
                      value={values.specialization}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.specialization && Boolean(errors.specialization)}
                      helperText={touched.specialization && errors.specialization}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      name="yearsOfExperience"
                      label="Years of Experience"
                      type="number"
                      value={values.yearsOfExperience}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.yearsOfExperience && Boolean(errors.yearsOfExperience)}
                      helperText={touched.yearsOfExperience && errors.yearsOfExperience}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <InputLabel>Availability</InputLabel>
                      <Select
                        name="isAvailable"
                        value={values.isAvailable ? 'true' : 'false'}
                        label="Availability"
                        onChange={(e) => {
                          handleChange({
                            target: {
                              name: 'isAvailable',
                              value: e.target.value === 'true',
                            },
                          } as React.ChangeEvent<any>);
                        }}
                      >
                        <MenuItem value="true">Available</MenuItem>
                        <MenuItem value="false">Unavailable</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleAddNewMechanicDialogClose}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  Create Mechanic
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Success and Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MechanicsPage; 